import { useState } from "react";
import type { MeResponse } from "../api/me";
import { logout } from "../api/auth";

const DISMISS_KEY = "pterocloud-ai:email-verify-dismissed";

function EmailVerifyBanner({ email }: { email: string }) {
  const [dismissed, setDismissed] = useState(
    typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY) === email,
  );
  if (dismissed) return null;
  return (
    <div className="topbar-banner" role="status">
      <span>
        Your AI-made commits will show as <strong>unverified</strong> on GitHub until you add{" "}
        <code>{email}</code> at{" "}
        <a
          href="https://github.com/settings/emails"
          target="_blank"
          rel="noopener noreferrer"
        >
          github.com/settings/emails
        </a>
        .
      </span>
      <button
        type="button"
        onClick={() => {
          try { localStorage.setItem(DISMISS_KEY, email); } catch {}
          setDismissed(true);
        }}
      >
        Got it
      </button>
    </div>
  );
}

type Props = {
  me: MeResponse | null;
  loading: boolean;
  error: Error | null;
  onToggleSidebar?: () => void;
  onManageUsers?: () => void;
  onDeploySettings?: () => void;
  onOpenCmdk?: () => void;
};

async function handleSignOut(externalUrl: string | null) {
  if (externalUrl) {
    window.location.href = externalUrl;
    return;
  }
  try {
    await logout();
  } catch {
    /* ignore — still reload to drop client state */
  }
  window.location.reload();
}

function Identity({ me, loading, error }: Props) {
  if (loading) {
    return <span className="topbar-loading" aria-live="polite">Loading…</span>;
  }
  if (error) {
    if (error.message === "unauthorized") {
      return <span className="topbar-unauth">Sign-in required</span>;
    }
    return <span className="topbar-error" title={error.message}>Identity unavailable</span>;
  }
  if (me) {
    return (
      <>
        <span className="topbar-email">{me.email}</span>
        <span className="topbar-divider" aria-hidden="true"> · </span>
        <span className={`topbar-role topbar-role-${me.role}`}>{me.role}</span>
      </>
    );
  }
  return null;
}

export default function TopBar({ me, loading, error, onToggleSidebar, onManageUsers, onDeploySettings, onOpenCmdk }: Props) {
  return (
    <>
      <header className="topbar" role="banner">
        {onToggleSidebar && (
          <button
            type="button"
            className="topbar-hamburger"
            aria-label="Toggle navigation"
            onClick={onToggleSidebar}
          >
            ☰
          </button>
        )}
        <h1>Ptero-Cloud AI Workspace</h1>
        {onOpenCmdk && me && (
          <button
            type="button"
            className="topbar-cmdk"
            onClick={onOpenCmdk}
            aria-label="Open command palette"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3-3" />
            </svg>
            Search projects, issues…
            <kbd>⌘K</kbd>
          </button>
        )}
        <div className="topbar-identity">
          <Identity me={me} loading={loading} error={error} />
          {me?.role === "admin" && onManageUsers && (
            <button type="button" className="topbar-manage" onClick={onManageUsers}>
              Manage users
            </button>
          )}
          {me?.role === "admin" && onDeploySettings && (
            <button type="button" className="topbar-manage" onClick={onDeploySettings}>
              Deploy settings
            </button>
          )}
          {me && (
            <button
              type="button"
              className="topbar-signout"
              onClick={() => void handleSignOut(me.signOutUrl)}
            >
              Sign out
            </button>
          )}
        </div>
      </header>
      {me && <EmailVerifyBanner email={me.email} />}
    </>
  );
}
